// Step 4 (Smartlead) - the workspace user id these mailboxes are created under.
//
// This is the Smartlead twin of StepWorkspace. Smartlead has no workspace list to
// pick from; instead it builds the Google consent URL from a single numeric
// workspace user id, and that id is the only thing binding this run to the
// customer's Smartlead workspace.
//
// Key validation resolves the ID, including for empty accounts through a
// temporary draft. Older API-created runs may still need manual entry or a
// new key validation. This identifier comes back on the run and is shown here.

import { useState } from 'react';

import { sqApi, sqErrorMessage } from '../sequencerApi';
import type { SqStepProps } from './shared';

const USER_ID_INPUT_ID = 'sq-sl-user-id';
const USER_ID_HINT_ID = 'sq-sl-user-id-hint';

export function StepUserId({ runId, detail, reload, goToStep, toast }: SqStepProps): JSX.Element {
  const { run } = detail;

  const [value, setValue] = useState(run.sl_user_id ?? '');
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState<string | null>(null);

  const resolved = Boolean(run.sl_user_id);

  async function save(): Promise<void> {
    const id = value.trim();
    if (!/^\d+$/.test(id)) {
      setError('The workspace user id must be digits only.');
      return;
    }
    setBusy(true);
    setError(null);
    try {
      await sqApi.setUserId(runId, id);
      await reload();
      await goToStep(5);
    } catch (err) {
      setError(sqErrorMessage(err));
      toast('error', sqErrorMessage(err));
    } finally {
      setBusy(false);
    }
  }

  return (
    <section className="sq-step">
      <div className="sq-step-head">
        <h2>Step 4 - Workspace user id</h2>
        <p>
          Smartlead builds the Google consent URL from your workspace user id. It is the one value
          that binds this run to the customer's Smartlead workspace.
        </p>
      </div>
      <div className="sq-step-body">
        {error && <div className="sq-notice sq-notice--err">{error}</div>}

        {resolved ? (
          <div className="sq-notice sq-notice--ok">
            Resolved from your workspace: <strong className="sq-mono">{run.sl_user_id}</strong>. It
            was read off the API key when it was validated - confirm to use it, or edit the API key
            step if it looks wrong.
          </div>
        ) : (
          <div className="sq-field">
            <label className="sq-field-label" htmlFor={USER_ID_INPUT_ID}>
              Workspace user id
            </label>
            <input
              id={USER_ID_INPUT_ID}
              aria-describedby={USER_ID_HINT_ID}
              className="sq-input sq-mono"
              type="text"
              inputMode="numeric"
              value={value}
              maxLength={40}
              autoComplete="off"
              spellCheck={false}
              placeholder="e.g. 217022"
              onChange={(e) => setValue(e.target.value)}
              onKeyDown={(e) => {
                if (e.key === 'Enter' && !busy) void save();
              }}
              disabled={busy}
            />
            <span className="sq-hint" id={USER_ID_HINT_ID}>
              Validate the API key in step 2 to fill this in automatically, including for an
              empty Smartlead account. You can also enter a known user ID here.
            </span>
          </div>
        )}

        <div className="sq-actions">
          <button
            type="button"
            className="sq-btn sq-btn--secondary"
            onClick={() => void goToStep(3)}
            disabled={busy}
          >
            Back
          </button>
          <button
            type="button"
            className="sq-btn sq-btn--primary"
            onClick={() => void save()}
            disabled={busy || value.trim() === ''}
          >
            {busy ? 'Saving...' : resolved ? 'Confirm & continue' : 'Continue'}
          </button>
        </div>
      </div>
    </section>
  );
}
